// src/components/majanga/MajangaService.js
import axios from 'axios';

const API_BASE_URL = 'https://api.jamboricg.com/api/majanga';

const getAuthHeaders = () => ({
  'Authorization': `Token ${localStorage.getItem('token')}`,
});

export const MajangaService = {

  // ════════════════════════════════════════════════════════════════
  // COMMUNITY GANGS
  // ════════════════════════════════════════════════════════════════

  getAllGangs: async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/community-gangs/?specification=Majanga`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getGangById: async (id) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/community-gangs/${id}/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  createGang: async (gangData) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/community-gangs/`, gangData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  updateGang: async (id, gangData) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/community-gangs/${id}/`, gangData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  deleteGang: async (id) => {
    try {
      const response = await axios.delete(`${API_BASE_URL}/community-gangs/${id}/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getGangDetails: async (gangId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/community-gangs/${gangId}/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Gang role-related gang endpoints
  getGangRolesList: async (gangId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/gangs/${gangId}/roles/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getGangRoleHolders: async (gangId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/gangs/${gangId}/role-holders/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // ════════════════════════════════════════════════════════════════
  // GANG MEMBERSHIPS
  // ════════════════════════════════════════════════════════════════

  getGangMembers: async (gangId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/gang-memberships/by-gang/?gang_id=${gangId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getGangRequest: async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/gang-memberships/`);
      return response.data.results || response.data || [];
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getGangRequestById: async (gangId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/gang-memberships/by-gang/?gang_id=${gangId}`);
      return response.data.results || response.data || [];
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  approveGangRequest: async (requestId, payload) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/memberships/${requestId}/approve/`,
        payload
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  rejectGangRequest: async (requestId, payload) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/gang-memberships/${requestId}/approve/`,
        { status: 'rejected', ...payload }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  deleteGangRequest: async (requestId) => {
    try {
      const response = await axios.delete(`${API_BASE_URL}/gang-memberships/${requestId}/`, { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getMemberRoles: async (membershipId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/memberships/${membershipId}/roles/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // ════════════════════════════════════════════════════════════════
  // CONTRIBUTIONS
  // ════════════════════════════════════════════════════════════════

  addContribution: async (contributionData) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/gang-contributions/`, contributionData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getMemberContributionHistory: async (membershipId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/gang-contributions/member_history/`,
        { params: { membership_id: membershipId } }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getContributionsByGang: async (gangId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/contributions/gang/${gangId}/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getUserContributions: async (userId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/contributions/user/${userId}/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // ════════════════════════════════════════════════════════════════
  // CFN INDIVIDUAL FORMS
  // ════════════════════════════════════════════════════════════════

  getIndividualForms: async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/cfn-forms/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  deleteCFNForm: async (formId) => {
    try {
      const response = await axios.delete(`${API_BASE_URL}/cfn-forms/${formId}/`, { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // ════════════════════════════════════════════════════════════════
  // SPONSOR REQUEST FORMS
  // ════════════════════════════════════════════════════════════════

  getActiveForms: async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/sponsor-request-forms/active/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  uploadSponsorForm: async (formData) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/sponsor-request-forms/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data', ...getAuthHeaders() } }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  trackFormDownload: async (formId) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/sponsor-request-forms/${formId}/download/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  deleteSponsorForm: async (formId) => {
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/sponsor-request-forms/${formId}/`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // ════════════════════════════════════════════════════════════════
  // SPONSOR REQUESTS
  // ════════════════════════════════════════════════════════════════

  getAllSponsorRequests: async (status = null) => {
    try {
      const url = status
        ? `${API_BASE_URL}/sponsor-requests/admin-view/?status=${status}`
        : `${API_BASE_URL}/sponsor-requests/admin-view/`;
      const response = await axios.get(url, { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getSponsorRequestById: async (requestId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/sponsor-requests/${requestId}/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  adminReviewRequest: async (requestId, payload) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/sponsor-requests/${requestId}/admin-review/`,
        payload,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getSponsorshipResponses: async (requestId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/sponsorship-responses/?sponsor_request=${requestId}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // ════════════════════════════════════════════════════════════════
  // SERVICE REQUESTS
  // ════════════════════════════════════════════════════════════════

  getAllServiceRequests: async (filters = {}) => {
    try {
      const params = new URLSearchParams(filters).toString();
      const response = await axios.get(`${API_BASE_URL}/service-requests/all-requests-public/${params ? '?' + params : ''}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getServiceRequestById: async (id) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/service-requests/${id}/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  submitServiceRequestForApproval: async (requestId) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/service-requests/${requestId}/submit-for-approval/`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getServiceRequestApprovalChain: async (requestId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/service-requests/${requestId}/approval-chain/`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  officerApproveServiceRequest: async (requestId, payload) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/service-requests/${requestId}/officer-approve/`,
        payload
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  adminFinalApproveServiceRequest: async (requestId, payload) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/service-requests/${requestId}/admin-final-approve/`,
        payload,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  updateServiceRequestStatus: async (requestId, payload) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/service-requests/${requestId}/update-status/`,
        payload
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // ════════════════════════════════════════════════════════════════
  // GANG ROLES
  // ════════════════════════════════════════════════════════════════

  getGangRoles: async (gangId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/gang-roles/?gang_id=${gangId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  initDefaultRoles: async (gangId) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/gang-roles/init-defaults/`, { gang_id: gangId });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  createCustomRole: async (payload) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/gang-roles/create-custom/`, payload);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getApprovalChain: async (gangId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/gang-roles/approval-chain/?gang_id=${gangId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  configureApprovalChain: async (gangId, chain, removeFromChain = []) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/gang-roles/configure-chain/`, {
        gang_id: gangId,
        chain,
        remove_from_chain: removeFromChain,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  updateRoleChainPosition: async (roleId, approvalOrder) => {
    try {
      const response = await axios.patch(`${API_BASE_URL}/gang-roles/${roleId}/update-chain-position/`, {
        approval_order: approvalOrder,
        can_approve_service_requests: approvalOrder !== null,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  assignRoleToMember: async (roleId, membershipId, notes = '') => {
    try {
      const response = await axios.post(`${API_BASE_URL}/gang-roles/${roleId}/assign/`, {
        membership_id: membershipId,
        notes,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  unassignRoleFromMember: async (roleId, membershipId) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/gang-roles/${roleId}/unassign/`, {
        membership_id: membershipId,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getRoleMembers: async (roleId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/gang-roles/${roleId}/members/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  deleteRole: async (roleId) => {
    try {
      const response = await axios.delete(`${API_BASE_URL}/gang-roles/${roleId}/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  updateRole: async (roleId, data) => {
    try {
      const response = await axios.patch(`${API_BASE_URL}/gang-roles/${roleId}/`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // ════════════════════════════════════════════════════════════════
  // MEMBER ROLES
  // ════════════════════════════════════════════════════════════════

  getMemberRolesByGang: async (gangId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/member-roles/?gang_id=${gangId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // ════════════════════════════════════════════════════════════════
  // SERVICE REQUEST APPROVALS (read-only chain steps)
  // ════════════════════════════════════════════════════════════════

  getServiceApprovals: async (serviceRequestId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/service-approvals/?service_request_id=${serviceRequestId}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
};

export default MajangaService;
// // MajangaService.js
// import axios from 'axios';
// // const API_BASE_URL = 'https://api.jamboricg.com/api/majanga'
// const API_BASE_URL = 'https://api.jamboricg.com/api/majanga';

// export const MajangaService = {
//     // Get all community gangs
//     getAllGangs: async () => {
//         try {
//             const response = await axios.get(`${API_BASE_URL}/community-gangs/?specification=Majanga`);
//             return response.data;
//         } catch (error) {
//             throw error.response?.data || error.message;
//         }
//     },

//     // Get a specific community gang by ID
//     getGangById: async (id) => {
//         try {
//             const response = await axios.get(`${API_BASE_URL}/community-gangs/${id}/`);
//             return response.data;
//         } catch (error) {
//             throw error.response?.data || error.message;
//         }
//     },

//     // Create a new community gang
//     createGang: async (gangData) => {
//         try {
//             const response = await axios.post(`${API_BASE_URL}/community-gangs/`, gangData);
//             return response.data;
//         } catch (error) {
//             throw error.response?.data || error.message;
//         }
//     },

//     // Update an existing community gang
//     updateGang: async (id, gangData) => {
//         try {
//             const response = await axios.put(`${API_BASE_URL}/community-gangs/${id}/`, gangData);
//             return response.data;
//         } catch (error) {
//             throw error.response?.data || error.message;
//         }
//     },

//     // Delete a community gang
//     deleteGang: async (id) => {
//         try {
//             const response = await axios.delete(`${API_BASE_URL}/community-gangs/${id}/`);
//             return response.data;
//         } catch (error) {
//             throw error.response?.data || error.message;
//         }
//     },

//     // Get gang members
//     getGangMembers: async (gangId) => {
//         try {
//             const response = await axios.get(`${API_BASE_URL}/gang-memberships/by_gang/?gang_id=${gangId}`);
//             return response.data;
//         } catch (error) {
//             throw error.response?.data || error.message;
//         }
//     },



//    getGangRequest: async () => {
//     try {
//         const response = await axios.get(`${API_BASE_URL}/gang-memberships/`);
//         // Return the results array directly, not the whole response object
//         return response.data.results || response.data || [];
//     } catch (error) {
//         throw error.response?.data || error.message;
//     }
// },
//     getGangRequestById: async (gangId) => {
//     try {
//         // Use the by_gang endpoint to get all memberships for a gang
//         const response = await axios.get(`${API_BASE_URL}/gang-memberships/by_gang/?gang_id=${gangId}`);
//         // Return results array
//         return response.data.results || response.data || [];
//     } catch (error) {
//         throw error.response?.data || error.message;
//     }
// },

//     // In MajangaService.js
//     approveGangRequest: async (requestId, payload) => {
//         try {
//             const response = await axios.post(
//                 `${API_BASE_URL}/gang-memberships/${requestId}/approve/`,
//                 payload
//             );
//             return response.data;
//         } catch (error) {
//             throw error.response?.data || error.message;
//         }
//     },

//     rejectGangRequest: async (requestId, payload) => {
//         try {
//             const response = await axios.post(
//                 `${API_BASE_URL}/gang-memberships/${requestId}/reject/`,
//                 payload
//             );
//             return response.data;
//         } catch (error) {
//             throw error.response?.data || error.message;
//         }
//     },

//     getGangDetails: async (gangId) => {
//         try {
//           const response = await axios.get(`${API_BASE_URL}/community-gangs/${gangId}/`);
//           return response.data;
//         } catch (error) {
//           console.error('Error fetching gang details:', error);
//           throw error;
//         }
//       },
    
//       addContribution: async (contributionData) => {
//         try {
//           const response = await axios.post(`${API_BASE_URL}/gang-contributions/`, contributionData);
//           return response.data;
//         } catch (error) {
//           console.error('Error adding contribution:', error);
//           throw error;
//         }
//       },
//       getMemberContributionHistory: async (membershipId) => {
//         try {
//             const response = await axios.get(
//                 `${API_BASE_URL}/gang-contributions/member_history/`,
//                 {
//                     params: {
//                         membership_id: membershipId
//                     }
//                 }
//             );
//             return response.data;
//         } catch (error) {
//             throw error.response?.data || error.message;
//         }
//     },
//     getIndividualForms: async () => {
//         try {
//             const response = await axios.get(`${API_BASE_URL}/cfn-forms/`);
//             return response.data;
//         } catch (error) {
//             throw error.response?.data || error.message;
//         }
//     },
//     deleteCFNForm: async (formId) => {
//         try {
//             const response = await axios.delete(`${API_BASE_URL}/cfn-forms/${formId}/`);
//             return response.data;
//         } catch (error) {
//             throw error.response?.data || error.message;
//         }
//     },

//     // SPONSOR REQUEST FORMS
//     getActiveForms: async () => {
//         try {
//             const response = await axios.get(`${API_BASE_URL}/sponsor-request-forms/active/`);
//             return response.data;
//         } catch (error) {
//             throw error.response?.data || error.message;
//         }
//     },

//     uploadSponsorForm: async (formData) => {
//         try {
//             const response = await axios.post(
//                 `${API_BASE_URL}/sponsor-request-forms/`,
//                 formData,
//                 {
//                     headers: {
//                         'Content-Type': 'multipart/form-data',
//                         'Authorization': `Token ${localStorage.getItem('token')}` 
//                     }
//                 }
//             );
//             return response.data;
//         } catch (error) {
//             throw error.response?.data || error.message;
//         }
//     },

//     trackFormDownload: async (formId) => {
//         try {
//             const response = await axios.post(
//                 `${API_BASE_URL}/sponsor-request-forms/${formId}/download/`
//             );
//             return response.data;
//         } catch (error) {
//             throw error.response?.data || error.message;
//         }
//     },

//     deleteSponsorForm: async (formId) => {
//         try {
//             const response = await axios.delete(
//                 `${API_BASE_URL}/sponsor-request-forms/${formId}/`,
//                 {
//                     headers: {
//                         'Content-Type': 'multipart/form-data',
//                         'Authorization': `Token ${localStorage.getItem('token')}` 
//                     }
//                 }
//             );
//             return response.data;
//         } catch (error) {
//             throw error.response?.data || error.message;
//         }
//     },

//     // SPONSOR REQUESTS
//     getAllSponsorRequests: async (status = null) => {
//         try {
//             console.log("token here ....",localStorage.getItem('token'));
//             const url = status 
//                 ? `${API_BASE_URL}/sponsor-requests/admin-view/?status=${status}`
//                 : `${API_BASE_URL}/sponsor-requests/admin-view/`;
//             const response = await axios.get(url, {
//                 headers: {
//                     'Authorization': `Token ${localStorage.getItem('token')}`
//                 }
//             });
//             return response.data;
//         } catch (error) {
//             throw error.response?.data || error.message;
//         }
//     },

//     getSponsorRequestById: async (requestId) => {
//         try {
//             const response = await axios.get(
//                 `${API_BASE_URL}/sponsor-requests/${requestId}/`
//             );
//             return response.data;
//         } catch (error) {
//             throw error.response?.data || error.message;
//         }
//     },

//     adminReviewRequest: async (requestId, payload) => {
//         try {
//             const response = await axios.post(
//                 `${API_BASE_URL}/sponsor-requests/${requestId}/admin-review/`,
//                 payload,
//                 {
//                     headers: {
//                         'Authorization': `Token ${localStorage.getItem('token')}` 
//                     }
//                 }
//             );
//             return response.data;
//         } catch (error) {
//             throw error.response?.data || error.message;
//         }
//     },

//     // SPONSORSHIP RESPONSES
//     getSponsorshipResponses: async (requestId) => {
//         try {
//             const response = await axios.get(
//                 `${API_BASE_URL}/sponsorship-responses/?sponsor_request=${requestId}`
//             );
//             return response.data;
//         } catch (error) {
//             throw error.response?.data || error.message;
//         }
//     },
// };

// export default MajangaService;